import {
  ChoiceInteractionComponent,
  TextEntryInteraction,
  ExtendedTextInteraction,
  AssociateInteractionComponent,
  OrderInteractionComponent,
  SimpleChoiceComponent,
  HotspotInteraction,
  HotspotChoice,
  SimpleAssociableChoiceComponent,
  MatchInteractionComponent,
  SimpleMatchSetComponent,
  InlineChoiceInteractionComponent,
  InlineChoiceComponent,
  GapMatchInteractionComponent,
  GapTextComponent,
  GapImgComponent,
  GapComponent,
  GraphicOrderInteraction,
  AssociableHotspot,
  GraphicAssociateInteraction,
  SelectPointInteraction,
  HotTextInteractionComponent,
  HotTextComponent,
  GraphicGapMatchInteraction,
  PositionObjectStage,
  PositionObjectInteraction,
  GridInInteractionComponent,
  GridInSymbolComponent,
  GridInSymbolsComponent,
  EquationEditorComponent,
  GraphingInteractionContainer,
  NumberLineComponent
} from "../../interactions"
import {
  InfoControl,
  Prompt,
  ObjectComponent,
  BlockQuoteComponent,
  RubricBlockComponent
} from "../info"
import { WithFeedback, FeedbackInline, FeedbackBlock } from "../../feedback"
import { MathMLComponent } from "../../mathML"
import { CustomSpan } from "../../ui/containers/CustomSpan"
import { Paragraph } from "../../ui/text/Paragraph"
import { CustomInteractionIds, DomNodes } from "../../constants"
import { Typography } from "@mui/material"
import parse, { HTMLReactParserOptions, domToReact } from "html-react-parser"
import { FC, memo, useCallback, useMemo } from "react"
import {
  getAccessibilityAttributes,
  getMpQtiDataAttributes,
  getStylesObjectFromString
} from "../../helpers"
import { PackagedImage } from "./PackagedImage"
import { TableScrollContainer, getTableAccessibleName } from "./TableScrollContainer"
import { handleTableArrowScroll } from "../../helpers/accessibilityHelpers/tableKeyboardScroll"
import {
  CheckCorrect,
  isQuestionComplete,
  onSaveStudentAnswer,
  ContainerType,
  NeutralSetting
} from "../../types"

interface ContentRendererProps {
  content: string
  itemId?: string
  resourceId?: string
  showAnswer?: boolean
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  containerType: ContainerType
  isNeutralSetting?: NeutralSetting
  // Used for fetching Tables in QTI Interaction
  QtiTableContent?: React.FC<{ tableId: string; identifier?: string }>
  // a11y: resolves the live Media Library alt for an image (Ready#7894/#7937); given the asset
  // id or src, returns the live altText (or undefined). Threaded through to PackagedImage.
  resolveAlt?: (assetIdOrSrc: string) => string | undefined
}

const ContentRenderer: FC<ContentRendererProps> = memo(
  ({
    content,
    itemId,
    resourceId,
    showAnswer = false,
    onSaveStudentAnswer,
    checkCorrect,
    isCompletedQuestion,
    containerType,
    isNeutralSetting,
    QtiTableContent,
    resolveAlt
  }) => {
    const options: HTMLReactParserOptions = useMemo(
      () => ({
        replace: domNode => renderComponent(domNode)
      }),
      // eslint-disable-next-line
      [itemId, resolveAlt]
    )

    const repairSelfClosingTags = (content: string) => {
      const selfClosingReg = /<(\w+)([^>]*)\/\s*>/g

      return content.replace(selfClosingReg, (source, tagName, content) => {
        // Don't replace "<br/>" tags, because it doubles breaks.
        // See https://gitlab.com/masteryprep/masteryprep-platform/qti-pwa/-/issues/13
        if (tagName === "br") return source

        return `<${tagName}${content}></${tagName}>`
      })
    }

    const transformStringToSolid = (content: string) => {
      return content.split("\n").join("").trim()
    }

    const getCustomInteractionComponent = useCallback(
      (
        props: any,
        children: React.ReactNode,
        responseDeclarationId: string
      ) => {
        const { id, ...rest } = props

        switch (id) {
          case CustomInteractionIds.gridIn: {
            return (
              <WithFeedback
                itemId={props.itemId}
                responseDeclarationId={responseDeclarationId}
                showCorrectAnswer
              >
                <GridInInteractionComponent {...rest}>
                  {children}
                </GridInInteractionComponent>
              </WithFeedback>
            )
          }
          case CustomInteractionIds.equationEditor: {
            return (
              <WithFeedback
                itemId={props.itemId}
                responseDeclarationId={responseDeclarationId}
                showCorrectAnswer
              >
                <EquationEditorComponent
                  {...rest}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  containerType={containerType}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </EquationEditorComponent>
              </WithFeedback>
            )
          }
          case CustomInteractionIds.graphing: {
            return (
              <GraphingInteractionContainer key={props.itemId} {...rest}>
                {children}
              </GraphingInteractionContainer>
            )
          }
          case CustomInteractionIds.numberLine: {
            return (
              <NumberLineComponent key={props.itemId} {...rest}>
                {children}
              </NumberLineComponent>
            )
          }
        }
      },
      []
    )

    const renderComponent = useCallback(
      (domNode: any) => {
        const props = domNode.attribs && {
          ...(itemId && { itemId: itemId }),
          componentNode: domNode.name,
          className: domNode.attribs.class,
          ...getAccessibilityAttributes(domNode.attribs),
          ...getStylesObjectFromString(domNode.attribs)
        }
        const children =
          domNode.children && domToReact(domNode.children, options)

        switch (domNode.name) {
          case DomNodes.span: {
            const inlineQuote = domNode.attribs?.["inline-quote"]
            if (inlineQuote) {
              return inlineQuote === "true" ? (
                <q>{children}</q>
              ) : (
                <q cite={inlineQuote}>{children}</q>
              )
            }
            return <CustomSpan {...props}>{children}</CustomSpan>
          }
          case DomNodes.div: {
            const { itemId, componentNode, ...restProps } = props
            return <div {...restProps}>{children}</div>
          }
          case DomNodes.typography: {
            const { itemId, componentNode, ...restProps } = props
            return <Paragraph {...restProps}>{children}</Paragraph>
          }
          case DomNodes.typographyH1:
          case DomNodes.typographyH2:
          case DomNodes.typographyH3:
          case DomNodes.typographyH4:
          case DomNodes.typographyH5:
          case DomNodes.typographyH6: {
            return (
              <Typography variant={domNode.name} className={props.className}>
                {children}
              </Typography>
            )
          }
          case DomNodes.image: {
            const { class: _class, ...rest } = props
            return (
              <PackagedImage
                {...rest}
                resourceId={resourceId}
                resolveAlt={resolveAlt}
              />
            )
          }
          case DomNodes.math: {
            return <MathMLComponent {...props}>{children}</MathMLComponent>
          }
          case DomNodes.table: {
            // Handle table rendering with data-table-id attribute
            const tableId = domNode.attribs?.["data-table-id"]
            const identifier = domNode.attribs?.["identifier"]
            if (tableId && QtiTableContent) {
              // Use the QtiTableContent component to fetch and render the table
              return (
                <QtiTableContent tableId={tableId} identifier={identifier} />
              )
            }
            // a11y: wrap a plain (non-delegated) table in a focusable, keyboard-scrollable
            // container so off-screen columns of a wide table are reachable without a mouse
            // (incl. Safari). SC 2.1.1.
            // a11y: getAccessibilityAttributes buckets role/aria-* under `accessibilityAttr`;
            // spread it onto the table so the authored ARIA survives (not a stray object prop).
            // TableScrollContainer makes a WIDE table a focusable scroll group and leaves a
            // normal-width one unchanged (kept in sync with qti-pwa). SC 2.1.1.
            const { itemId, componentNode, accessibilityAttr, class: _class, ...tableProps } = props
            return (
              <TableScrollContainer
                accessibleName={getTableAccessibleName(domNode)}
                onKeyDown={handleTableArrowScroll}
              >
                <table {...tableProps} {...accessibilityAttr}>{children}</table>
              </TableScrollContainer>
            )
          }
          case DomNodes.textEntryInteraction: {
            return (
              <TextEntryInteraction {...props}>{children}</TextEntryInteraction>
            )
          }
          case DomNodes.infoControl: {
            return <InfoControl {...props}>{children}</InfoControl>
          }
          case DomNodes.choiceInteraction: {
            const { startLetter } = getMpQtiDataAttributes(domNode.attribs)
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                useByAnswerFeedback
              >
                <ChoiceInteractionComponent
                  {...props}
                  startLetter={startLetter}
                  showAnswer={showAnswer}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  containerType={containerType}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </ChoiceInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.simpleChoice: {
            return (
              <SimpleChoiceComponent {...props}>
                {children}
              </SimpleChoiceComponent>
            )
          }
          case DomNodes.prompt: {
            return <Prompt {...props}>{children}</Prompt>
          }
          case DomNodes.feedbackInline: {
            return <FeedbackInline {...props}>{children}</FeedbackInline>
          }
          case DomNodes.feedbackBlock: {
            return <FeedbackBlock {...props}>{children}</FeedbackBlock>
          }
          case DomNodes.extendedTextInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                forceMultiscore
                multiScoreFormatter={(raw, possible) =>
                  `${raw} of ${possible} Points Received`
                }
              >
                <ExtendedTextInteraction
                  {...props}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </ExtendedTextInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.associateInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <AssociateInteractionComponent {...props}>
                  {children}
                </AssociateInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.simpleAssociableChoice: {
            const parentNodeName = domNode?.parent?.name
            return (
              <SimpleAssociableChoiceComponent
                parentNodeName={parentNodeName}
                {...props}
              >
                {children}
              </SimpleAssociableChoiceComponent>
            )
          }
          case DomNodes.orderInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <OrderInteractionComponent
                  {...props}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </OrderInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.hotspotInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                useByAnswerFeedback
              >
                <HotspotInteraction
                  {...props}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  containerType={containerType}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </HotspotInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.hotspotChoice: {
            return <HotspotChoice {...props}>{children}</HotspotChoice>
          }
          case DomNodes.object: {
            return (
              <ObjectComponent
                {...props}
                resourceId={resourceId}
                resolveAlt={resolveAlt}
              >
                {children}
              </ObjectComponent>
            )
          }
          case DomNodes.matchInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                useByAnswerFeedback
              >
                <MatchInteractionComponent
                  {...props}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  containerType={containerType}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </MatchInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.simpleMatchSet: {
            return (
              <SimpleMatchSetComponent {...props}>
                {children}
              </SimpleMatchSetComponent>
            )
          }
          case DomNodes.inlineChoiceInteraction: {
            return (
              <InlineChoiceInteractionComponent
                {...props}
                onSaveStudentAnswer={onSaveStudentAnswer}
                checkCorrect={checkCorrect}
                isCompletedQuestion={isCompletedQuestion}
                isNeutralSetting={isNeutralSetting}
                containerType={containerType}
              >
                {children}
              </InlineChoiceInteractionComponent>
            )
          }
          case DomNodes.inlineChoice: {
            return (
              <InlineChoiceComponent {...props}>
                {children}
              </InlineChoiceComponent>
            )
          }
          case DomNodes.gapMatchInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                useByAnswerFeedback
              >
                <GapMatchInteractionComponent
                  {...props}
                  isCompletedQuestion={isCompletedQuestion}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  isNeutralSetting={isNeutralSetting}
                  containerType={containerType}
                >
                  {children}
                </GapMatchInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.gapText: {
            return <GapTextComponent {...props}>{children}</GapTextComponent>
          }
          case DomNodes.gapImg: {
            return <GapImgComponent {...props}>{children}</GapImgComponent>
          }
          case DomNodes.gap: {
            return (
              <GapComponent
                checkCorrect={checkCorrect}
                isCompletedQuestion={isCompletedQuestion}
                isNeutralSetting={isNeutralSetting}
                {...props}
              >
                {children}
              </GapComponent>
            )
          }
          case DomNodes.blockQuote: {
            return (
              <BlockQuoteComponent {...props}>{children}</BlockQuoteComponent>
            )
          }
          case DomNodes.graphicOrderInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <GraphicOrderInteraction {...props}>
                  {children}
                </GraphicOrderInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.associableHotspot: {
            return <AssociableHotspot {...props}>{children}</AssociableHotspot>
          }
          case DomNodes.graphicAssociateInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <GraphicAssociateInteraction {...props}>
                  {children}
                </GraphicAssociateInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.selectPointInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <SelectPointInteraction {...props}>
                  {children}
                </SelectPointInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.hotTextInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
                useByAnswerFeedback
              >
                <HotTextInteractionComponent
                  {...props}
                  onSaveStudentAnswer={onSaveStudentAnswer}
                  checkCorrect={checkCorrect}
                  isCompletedQuestion={isCompletedQuestion}
                  containerType={containerType}
                  isNeutralSetting={isNeutralSetting}
                >
                  {children}
                </HotTextInteractionComponent>
              </WithFeedback>
            )
          }
          case DomNodes.hotText: {
            return (
              <HotTextComponent
                {...props}
                checkCorrect={checkCorrect}
                isCompletedQuestion={isCompletedQuestion}
                isNeutralSetting={isNeutralSetting}
              >
                {children}
              </HotTextComponent>
            )
          }
          case DomNodes.graphicGapMatchInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <GraphicGapMatchInteraction key={props.itemId} {...props}>
                  {children}
                </GraphicGapMatchInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.positionObjectStage: {
            return (
              <PositionObjectStage {...props}>{children}</PositionObjectStage>
            )
          }
          case DomNodes.positionObjectInteraction: {
            return (
              <WithFeedback
                itemId={itemId}
                responseDeclarationId={domNode.attribs.responseidentifier}
              >
                <PositionObjectInteraction {...props}>
                  {children}
                </PositionObjectInteraction>
              </WithFeedback>
            )
          }
          case DomNodes.rubricBlock: {
            return (
              <RubricBlockComponent {...props}>{children}</RubricBlockComponent>
            )
          }
          case DomNodes.customInteraction: {
            return getCustomInteractionComponent(
              props,
              children,
              domNode.attribs.responseidentifier
            )
          }
          case DomNodes.gridInSymbols: {
            return (
              <GridInSymbolsComponent {...props}>
                {children}
              </GridInSymbolsComponent>
            )
          }
          case DomNodes.gridInSymbol: {
            return (
              <GridInSymbolComponent {...props}>
                {children}
              </GridInSymbolComponent>
            )
          }
        }
      },
      [
        itemId,
        options,
        resourceId,
        getCustomInteractionComponent,
        showAnswer,
        onSaveStudentAnswer,
        checkCorrect,
        isCompletedQuestion,
        containerType,
        isNeutralSetting,
        QtiTableContent,
        resolveAlt
      ]
    )

    return (
      <>
        {content &&
          parse(
            repairSelfClosingTags(transformStringToSolid(content)),
            options
          )}
      </>
    )
  }
)

export default ContentRenderer
